import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, MenuItem, Link as MuiLink, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { api } from '../services/api';

export default function RegisterPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [role, setRole] = useState('inspector');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || !fullName.trim()) {
      setError('All fields are required.');
      return;
    }
    if (role === 'admin' && !companyName.trim()) {
      setError('Company Name is required when registering as an admin.');
      return;
    }

    setLoading(true); setError('');
    try {
      await api.register(email, password, fullName, role, companyName || undefined, invitationToken || undefined);
      navigate('/login', { state: { message: 'Registration successful! Please log in.' } });
    } catch (err: any) {
      const parseError = (error: any) => {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) return detail.map(d => d.msg).join(', ');
        if (typeof detail === 'string') return detail;
        return error?.message || 'Registration failed';
      };
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ width: 400, bgcolor: 'background.paper', borderRadius: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.04)}` }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', textAlign: 'center', mb: 0.5 }}>
            Surface IQ
          </Typography>
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 4, fontSize: 14 }}>
            Create Your Account
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleRegister}>
            <TextField
              fullWidth label="Full Name" value={fullName}
              onChange={e => setFullName(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
            <TextField
              fullWidth label="Email" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
            <TextField
              fullWidth label="Password" type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
            <TextField
              fullWidth label="Company Name (required for admin)" value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
            <TextField
              fullWidth label="Invitation Token (optional)" value={invitationToken}
              onChange={e => setInvitationToken(e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            />
            <TextField
              select fullWidth label="Role" value={role}
              onChange={e => setRole(e.target.value)}
              sx={{ mb: 3 }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              <MenuItem value="inspector">Inspector</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              sx={{ py: 1.5, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, fontWeight: 'bold', fontSize: 15, mb: 2 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'REGISTER'}
            </Button>
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 14 }}>
              Already have an account?{' '}
              <MuiLink component={Link} to="/login" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                Log in
              </MuiLink>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}